// Which Home Screen hint a visit gets, from the browser and the person's
// earlier answer. No DOM in here, so a node test covers every case; the
// component reads the browser into a HintEnv and asks.
export type HintVariant = 'ios' | 'android' | 'desktop' | 'none';

export interface HintEnv {
  /** navigator.userAgent */
  ua: string;
  /** navigator.platform: 'MacIntel' on an iPad that asks for desktop sites. */
  platform: string;
  /** navigator.maxTouchPoints: an iPad with a Mac user agent still has a touch screen. */
  maxTouchPoints: number;
  /** Already opened from the Home Screen or the Dock: (display-mode: standalone), or navigator.standalone on iOS. */
  standalone: boolean;
  /** Dismissed once before, on this device. */
  dismissed: boolean;
  /** Chrome (or Edge) offered its install prompt and we hold it. */
  canPrompt: boolean;
  /** location.search: ?hint shows the hint regardless, ?hint=<variant> forces one. */
  search: string;
}

export const DISMISSED_KEY = 'hd:hintDismissed';
const VARIANTS: readonly string[] = ['ios', 'android', 'desktop', 'none'];

export const isIOS = ({ ua, platform, maxTouchPoints }: Pick<HintEnv, 'ua' | 'platform' | 'maxTouchPoints'>): boolean =>
  /iPhone|iPad|iPod/.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);

/** Safari itself on a Mac: every other Mac browser adds its own name after "Safari/". */
export const isMacSafari = ({ ua, platform }: Pick<HintEnv, 'ua' | 'platform'>): boolean =>
  platform.startsWith('Mac') && /Safari\//.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS/.test(ua);

/** The variant this browser would get, before standalone and dismissal are considered. */
export function detectHint(env: HintEnv): HintVariant {
  if (isIOS(env)) return 'ios';
  if (env.canPrompt) return 'android';
  if (isMacSafari(env)) return 'desktop';
  return 'none';
}

export function chooseHint(env: HintEnv): HintVariant {
  const forced = new URLSearchParams(env.search).get('hint');
  if (forced !== null) {
    // ?hint=<variant> forces one; a bare ?hint (or an unknown value) shows the real one regardless.
    return VARIANTS.includes(forced) ? (forced as HintVariant) : detectHint(env);
  }
  if (env.standalone || env.dismissed) return 'none';
  return detectHint(env);
}

/** The words, per variant. The iOS pair is the prototype's. */
export const HINT_COPY = {
  ios: { title: 'Add Honey-Do to your Home Screen', body: 'Tap Share, then “Add to Home Screen”. It opens like an app.' },
  android: { title: 'Add Honey-Do to your Home Screen', body: 'Install it. It opens like an app, with no browser around it.' },
  desktop: { title: 'Add Honey-Do to your Dock', body: 'In Safari, choose File, then “Add to Dock”. It opens like an app.' },
} as const;
